import type { RouteType } from "../../../types/RouteType";
import { ManholeViewStore } from "./stores/ManholeViewStore";
import { runInAction } from "mobx";

const ManholeRoute: RouteType = {
  name: "Manhole Designer",
  path: "/manhole-design",
  defaultQueryParams: { configTab: 'project' },
  layout: "/app",
  requiresAuth: true,
  requiresPermission: false,
  group: "Estimating",
  /**
   * Hidden from the menu and omnibar (2026-08-31, NTX).
   *
   * This is the OLD manhole tool — it lists as "Manhole Stack Designer", which
   * reads like a sibling of the current one rather than its predecessor.
   * @intrasync/stack-designer's "IntraSync Designer" replaced it and is the only tool
   * NTX designs out of now; it is also the app's `defaultRoute`, so offering
   * both in the dropdown invites someone to open the wrong one and lose the
   * work.
   *
   * Hidden, not deleted. The route still resolves by name and URL, and 491
   * manhole_project_saves in production came out of this screen — so it stays
   * reachable for anything that needs to open historical work. Delete it only
   * once that history is confirmed readable in the new designer.
   */
  hidden: true,
  viewportCard: {
    header: {
      component: () => import("./components/ManholeHeader"),
      title: "Manhole Stack Designer",
      color: "primary",
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./components/ManholeBody"),
      grayBackground: true,
    },
  },

  beforeEnter: async ({ rootStore, queryParams }) => {
    try {
      // Load accounts for dropdown selection
      if ((rootStore as any).customersStore) {
        await (rootStore as any).customersStore.find();
      }

      // Load catalog products if not already loaded
      if ((rootStore as any).manholeComponentsStore?.entities.length === 0) {
        await (rootStore as any).manholeComponentsStore.find({
          query: { $limit: 5000 },
        });
      }

      // Load structure codes for component filtering
      if ((rootStore as any).structureCodesStore) {
        await (rootStore as any).structureCodesStore.find({
          query: { isActive: true }
        });
      }

      // Load structure code component assignments
      if ((rootStore as any).structureCodeComponentsStore) {
        await (rootStore as any).structureCodeComponentsStore.find({
          query: { $limit: 5000 },
        });
      }

      // Load plant products for filtering
      if ((rootStore as any).plantProductsStore) {
        await (rootStore as any).plantProductsStore.find({
          query: { $limit: 5000 },
        });
      }

      // If no components found, try loading from products service with filter
      if (
        (rootStore as any).manholeComponentsStore?.entities.length === 0 &&
        (rootStore as any).productsStore
      ) {
        await (rootStore as any).productsStore.find({
          query: { $limit: 5000 },
        });
      }

      // Load user's projects
      await (rootStore as any).manholeProjectsStore.find();

      // If projectId provided via query params, load the project and its structures
      if (queryParams?.projectId) {
        const project = (rootStore as any).manholeProjectsStore?.entities?.find(
          (p: any) =>
            p.id === queryParams.projectId || p.projectId === queryParams.projectId
        );

        if (!project) {
          await (rootStore as any).manholeProjectsStore.get(queryParams.projectId);
        }

        // Load structures for this project
        let structures: any[] = [];
        if ((rootStore as any).manholeStructuresStore) {
          const structureResult = await (
            rootStore as any
          ).manholeStructuresStore.find({
            query: { projectId: queryParams.projectId },
          });
          // Filter to ensure only structures for THIS project are included
          // (protects against stale cached data from other projects)
          structures = (structureResult?.data || []).filter(
            (s: any) => s.projectId === queryParams.projectId
          );
        }

        // Sort structures by sequenceOrder to get first structure correctly
        const sortedStructures = [...structures].sort((a: any, b: any) => {
          if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
            return a.sequenceOrder - b.sequenceOrder;
          }
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        });

        // Get the target structure (from queryParams or first one in sorted order)
        const targetStructureId = queryParams?.structureId || sortedStructures[0]?.id;

        // If we have structures but no structureId in URL, redirect to include it
        // Validate that targetStructureId belongs to this project before updating URL
        if (!queryParams?.structureId && targetStructureId && sortedStructures.length > 0) {
          const validStructure = sortedStructures.find(
            (s: any) => s.id === targetStructureId && s.projectId === queryParams.projectId
          );
          if (validStructure) {
            // Use setTimeout to allow beforeEnter to complete, then redirect
            setTimeout(() => {
              rootStore.routerStore.goTo("Manhole Designer", null, {
                ...queryParams,
                projectId: queryParams.projectId as string,
                structureId: targetStructureId,
              });
            }, 0);
          }
        }

        if (targetStructureId) {
          // Load pipes for this structure
          if ((rootStore as any).manholePipeConnectionsStore) {
            await (rootStore as any).manholePipeConnectionsStore.find({
              query: {
                structureId: targetStructureId,
                $sort: { pipeNumber: 1 },
              },
            });
          }

          // Load sequences (components) for this structure
          if ((rootStore as any).manholeSequencesStore) {
            await (rootStore as any).manholeSequencesStore.find({
              query: {
                structureId: targetStructureId,
                $sort: { sequenceOrder: 1 },
              },
            });
          }

          // Load internals for this structure
          if ((rootStore as any).manholeInternalsStore) {
            await (rootStore as any).manholeInternalsStore.find({
              query: { structureId: targetStructureId },
            });
          }
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to load manhole designer data:", error);
      return false;
    }
  },

  onEnter: async ({ rootStore, queryParams }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Set active project if projectId provided via query params
    if (queryParams?.projectId) {
      await viewStore.setActiveProject(queryParams.projectId as string);

      // Set currentStructureId for internal tracking (activeStructureId is computed from URL)
      const structureId = queryParams?.structureId as string || viewStore.structureOrder?.[0];
      if (structureId) {
        runInAction(() => {
          viewStore.currentStructureId = structureId;
        });
      }
    }

    // Restore canvas view based on persisted config tab
    const configTab = queryParams?.configTab || 'project';
    runInAction(() => {
      viewStore.canvasViewMode = configTab === 'components' ? 'side' : 'top';
    });
  },

  onParamsChange: async ({ rootStore, queryParams, previousQueryParams }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Handle project change via query params
    if (queryParams?.projectId !== previousQueryParams?.projectId) {
      if (queryParams?.projectId) {
        const project = (rootStore as any).manholeProjectsStore?.entities?.find(
          (p: any) =>
            p.id === queryParams.projectId || p.projectId === queryParams.projectId
        );

        if (!project) {
          await (rootStore as any).manholeProjectsStore.get(queryParams.projectId);
        }

        // Load structures for this project
        let structures: any[] = [];
        if ((rootStore as any).manholeStructuresStore) {
          const structureResult = await (
            rootStore as any
          ).manholeStructuresStore.find({
            query: { projectId: queryParams.projectId },
          });
          // Filter to ensure only structures for THIS project are included
          // (protects against stale cached data from other projects)
          structures = (structureResult?.data || []).filter(
            (s: any) => s.projectId === queryParams.projectId
          );
        }

        // Sort structures by sequenceOrder to get first structure correctly
        const sortedStructures = [...structures].sort((a: any, b: any) => {
          if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
            return a.sequenceOrder - b.sequenceOrder;
          }
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        });

        // Get the target structure (from queryParams or first one in sorted order)
        const targetStructureId = queryParams?.structureId || sortedStructures[0]?.id;

        // Load ALL structure-related data (pipes, sequences, internals)
        if (targetStructureId) {
          // Load pipes for this structure
          if ((rootStore as any).manholePipeConnectionsStore) {
            await (rootStore as any).manholePipeConnectionsStore.find({
              query: {
                structureId: targetStructureId,
                $sort: { pipeNumber: 1 },
              },
            });
          }

          // Load sequences (components) for this structure
          if ((rootStore as any).manholeSequencesStore) {
            await (rootStore as any).manholeSequencesStore.find({
              query: {
                structureId: targetStructureId,
                $sort: { sequenceOrder: 1 },
              },
            });
          }

          // Load internals for this structure
          if ((rootStore as any).manholeInternalsStore) {
            await (rootStore as any).manholeInternalsStore.find({
              query: { structureId: targetStructureId },
            });
          }
        }

        // Set active project in viewStore and update currentStructureId for internal tracking
        // (activeStructureId is computed from URL queryParams)
        await viewStore.setActiveProject(queryParams.projectId as string);
        if (targetStructureId) {
          runInAction(() => {
            viewStore.currentStructureId = targetStructureId;
          });
        }

        // If we loaded structures but no structureId was in URL, update URL to include it
        // Validate that targetStructureId actually belongs to this project's structures
        if (!queryParams?.structureId && targetStructureId && sortedStructures.length > 0) {
          const validStructure = sortedStructures.find(
            (s: any) => s.id === targetStructureId && s.projectId === queryParams.projectId
          );
          if (validStructure) {
            rootStore.routerStore.goTo("Manhole Designer", null, {
              ...queryParams,
              projectId: queryParams.projectId as string,
              structureId: targetStructureId,
            });
          }
        }
      } else {
        viewStore.setActiveProject(null);
      }
    }
    // Handle structure change within the same project
    else if (
      queryParams?.structureId !== previousQueryParams?.structureId &&
      queryParams?.structureId
    ) {
      // Load structure-related data for the new structure
      const structureId = queryParams.structureId as string;

      // Load pipes for this structure
      if ((rootStore as any).manholePipeConnectionsStore) {
        await (rootStore as any).manholePipeConnectionsStore.find({
          query: {
            structureId: structureId,
            $sort: { pipeNumber: 1 },
          },
        });
      }

      // Load sequences (components) for this structure
      if ((rootStore as any).manholeSequencesStore) {
        await (rootStore as any).manholeSequencesStore.find({
          query: {
            structureId: structureId,
            $sort: { sequenceOrder: 1 },
          },
        });
      }

      // Load internals for this structure
      if ((rootStore as any).manholeInternalsStore) {
        await (rootStore as any).manholeInternalsStore.find({
          query: { structureId: structureId },
        });
      }

      // Update currentStructureId for internal tracking
      // (activeStructureId is computed from URL queryParams)
      runInAction(() => {
        viewStore.currentStructureId = structureId;
      });
    }
    // Handle configTab change (canvas view sync for browser back/forward)
    else if (queryParams?.configTab !== previousQueryParams?.configTab) {
      const tabName = queryParams?.configTab || 'project';
      runInAction(() => {
        viewStore.canvasViewMode = tabName === 'components' ? 'side' : 'top';
      });
    }
  },

  beforeExit: async ({ rootStore }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Auto-save current structure before leaving the route
    if (viewStore.currentStructureIsDirty && viewStore.currentStructureId) {
      try {
        await viewStore.saveCurrentStructure();
      } catch (error) {
        console.error("Auto-save on exit failed:", error);
        // Ask user if they want to leave despite save failure
        const confirmed = window.confirm(
          "Failed to auto-save changes. Are you sure you want to leave? Changes may be lost."
        );
        return confirmed;
      }
    }

    return true;
  },

  onExit: ({ rootStore }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;
    viewStore.cleanup();
  },

  dialogs: [
    {
      name: "SaveProjectDialog",
      component: () => import("./dialogs/SaveProjectDialog"),
      title: "Save Manhole Project",
      cancelButton: true,
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        const formData = viewStore.dialogFormData;

        viewStore.updateEditingProject(formData);
        await viewStore.saveProject();

        if (!viewStore.error) {
          viewStore.clearDialogFormData();
          rootStore.routerStore.closeDialog();
          return true;
        }

        return false;
      },
      onSubmitValidation: ({ rootStore }) => {
        const viewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        const formData = viewStore.dialogFormData;

        // Check that required fields have actual values (not empty strings)
        // Check for selectedCustomerId (UI field) since accountId might be undefined
        const hasCustomer =
          formData?.selectedCustomerId && formData.selectedCustomerId !== "";
        const hasStructureId =
          formData?.structureId && formData.structureId.trim() !== "";

        return Boolean(hasCustomer && hasStructureId);
      },
    },
    {
      name: "LoadProjectDialog",
      component: () => import("./dialogs/LoadProjectDialog"),
      title: "Load Manhole Project",
      height: 400,
      cancelButton: true,
      submitButtonText: "", // Handle selection internally
    },
    {
      name: "ExportPDFDialog",
      component: () => import("./dialogs/ExportPDFDialog"),
      title: "Export to PDF",
      cancelButton: true,
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        const formData = (rootStore as any).routerStore?.dialogFormData || {};

        // Generate PDF (implemented in Phase 6)
        try {
          const { generatePDF } = await import("./utils/pdfGenerator");
          await generatePDF(viewStore, formData);
          rootStore.routerStore.closeDialog();
          return true;
        } catch (error) {
          console.error("PDF generation failed:", error);
          return false;
        }
      },
    },
    // NEW: Reset confirmation dialog (Phase 1)
    {
      name: "ResetConfirmationDialog",
      component: () => import("./dialogs/ResetConfirmationDialog"),
      title: "Reset Design",
      height: 280,
      cancelButton: true,
      submitButtonText: "Reset",
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;

        // Reset changes to the current project
        if (viewStore.activeProject) {
          // If there's an active project, restart editing with the original values
          viewStore.cancelEditing();
          viewStore.startEditingProject(viewStore.activeProject);
        } else if (viewStore.editingProject) {
          // If it's a new project (no activeProject), reset to empty values
          viewStore.startEditingProject();
        }

        // Reset height dismiss state
        (viewStore as any).resetHeightDismiss?.();

        rootStore.routerStore.closeDialog();
        return true;
      },
    },
    // NEW: Phase 7 - Print Preview Dialogs
    {
      name: "SubmittalPreviewDialog",
      component: () => import("./dialogs/SubmittalPreviewDialog"),
      title: "Submittal Preview",
      height: 800,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "QuotePreviewDialog",
      component: () => import("./dialogs/QuotePreviewDialog"),
      title: "Quote Preview",
      height: 800,
      cancelButton: true,
      submitButtonText: "Create Quote",
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        const formData = (rootStore as any).routerStore?.dialogFormData || {};

        const result = await viewStore.createQuote(formData);
        if (result) {
          (rootStore as any).notificationStore?.addNotification({
            message: `Quote ${result.quoteNumber} created successfully`,
            type: "success",
          });
          rootStore.routerStore.closeDialog();
        }
        return !!result;
      },
    },
    {
      name: "DesignReviewDialog",
      component: () => import("./dialogs/DesignReviewDialog"),
      title: "Design Review",
      height: 800,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "ComponentCatalogDialog",
      component: () => import("./dialogs/ComponentCatalogDialog"),
      title: "Select Component to Add",
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "ManageStructureCodesDialog",
      component: () => import("./dialogs/ManageStructureCodesDialog"),
      title: "Manage Structure Codes",
      fullWidth: true,
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "ManagePlantProductsDialog",
      component: () => import("./dialogs/ManagePlantProductsDialog"),
      title: "Manage Plant Products",
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "ManageComponentsDialog",
      component: () => import("./dialogs/ManageComponentsDialog"),
      title: "Manage Components",
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "ManagePipesDialog",
      component: () => import("./dialogs/ManagePipesDialog"),
      title: "Manage Pipes",
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "PipePresetCatalogDialog",
      component: () => import("./dialogs/PipePresetCatalogDialog"),
      title: "Select Pipe Configuration",
      height: 600,
      cancelButton: true,
      submitButtonText: "",
    },
    {
      name: "SalesOrderConversionDialog",
      component: () => import("./dialogs/SalesOrderConversionDialog"),
      title: "Convert to Sales Order",
      height: 800,
      cancelButton: true,
      submitButtonText: "",
    },
  ],
};

export default ManholeRoute;
